import { ReactElement, useState } from "react"
import styled from "styled-components"
import { DisplayImageProps } from "../types"
import FullscreenImageViewer from "./FullscreenImageViewer"
import { useTranslation } from "../TranslationProvider"

export default function DisplayImage({
  source,
  alt,
  url,
  maxHeight,
}: DisplayImageProps): ReactElement {
  const { t } = useTranslation()
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false)

  const handleClick = () => {
    if (url) {
      // Open URL in new tab
      window.open(url, "_blank", "noopener,noreferrer")
    } else {
      // Open fullscreen image viewer (current behavior)
      setIsFullscreenOpen(true)
    }
  }

  const closeFullscreen = () => {
    setIsFullscreenOpen(false)
  }

  return (
    <>
      <ImageWrapper>
        <ClickableImage
          src={source}
          alt={alt}
          onClick={handleClick}
          title={
            url
              ? t("accessibility.clickToOpenLink")
              : t("accessibility.clickToViewFullscreen")
          }
          $maxHeight={maxHeight}
        />
      </ImageWrapper>

      {/* Only render FullscreenImageViewer if no URL is provided */}
      {!url && (
        <FullscreenImageViewer
          src={source}
          alt={alt}
          isOpen={isFullscreenOpen}
          onClose={closeFullscreen}
        />
      )}
    </>
  )
}

const ImageWrapper = styled.div`
  position: relative;
`

const ClickableImage = styled.img<{ $maxHeight?: string }>`
  max-width: 100%;
  max-height: ${(props) => props.$maxHeight || "none"};
  border-radius: 1rem;
  border: 0.2rem solid var(--white);
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--white);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }

  &:focus {
    outline: 2px solid var(--special);
    outline-offset: 4px;
  }
`
